import { catchError, finalize, map, tap } from 'rxjs/operators';
import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';

import { APP_CONFIG, IAppConfig } from './../../../app.config';
import { Dwelling, DwellingPostTarget } from './../../model/dwelling';
import { Article } from '../../model/article';
import { RentalObjectService } from './../base/rental-object';
import { ProgressBarService } from '../../../core/service/progress-bar.service';
import { MessageService } from '../../../core/service/message.service';
import { Observable } from 'rxjs';
import { DwellingInspection } from '../../../dwelling-inspection/model/dwelling-inspection';
import { RentalObjectType } from '../../model/rental-object-type';
import { AutoCompleteSelectionItem, IAutoCompleteService } from '../../../core/component/autocomplete-remote/autocomplete-remote.component';
import { FormOfLease } from '../../model/const/enums';
import { LocalDate } from '../../../core/model/local-date';
import { BrokerInformation } from '../../model/broker-information';
import { IPostTargetLoader } from '../base/post-target-loader.interface';
import { HomeqDefaultsModel, HomeqCompanyOptions, MultipleSendToHomeQ, HQCompanyModel } from '../../model/homeq-office';

@Injectable()
export class DwellingService extends RentalObjectService<Dwelling> implements IAutoCompleteService, IPostTargetLoader<DwellingPostTarget> {
  private readonly _defaultFilterFields: string[] = ['companyIds', 'propertyIds', 'buildingIds', 'entranceIds', 'street'];

  constructor(
    client: HttpClient,
    @Inject(APP_CONFIG) config: IAppConfig,
    private _messageService: MessageService,
    progressBar: ProgressBarService
  ) {
    super(client, config, _messageService, Dwelling, progressBar, 'dwelling');
  }

  getValidFormsOfLease(companyId: string): Observable<FormOfLease[]> {
    return this.client.get<FormOfLease[]>(`${this.endpoint}/formsOfLease/${companyId}`);
  }

  getSelectionItem(id: string): Observable<AutoCompleteSelectionItem> {
    return this.client.get<Dwelling>(`${this.endpoint}/selectionItem/${id}`).pipe(
      map(data => this.createAutoCompleteSelectionItem(this.createModel(data))),
      catchError(e => {
        this._messageService.showError(e);
        return [];
      })
    );
  }

  getSelection(filter: string, options?: any, page?: number): Observable<AutoCompleteSelectionItem[]> {
    let queryArgs = {
      filter: filter,
      maxItems: 20,
    };
    if (options) queryArgs['options'] = options;
    if (page && page > 0) queryArgs['page'] = page;

    return this.client.post<Dwelling[]>(`${this.endpoint}/selection`, queryArgs).pipe(
      map(data => data.map(item => this.createAutoCompleteSelectionItem(this.createModel(item)))),
      catchError(e => {
        this._messageService.showError(e);
        throw e;
      })
    );
  }

  private createAutoCompleteSelectionItem(dwelling: Dwelling): AutoCompleteSelectionItem {
    let caption = dwelling.toString();
    return new AutoCompleteSelectionItem(caption, dwelling.id, dwelling, caption);
  }

  sendToHomeQ(model: MultipleSendToHomeQ): Observable<void> {
    this.progressBar.start();
    return this.client.post<any>(`${this.endpoint}/send-homeq`, model).pipe(
      tap(() => {
        this._messageService.showSuccess();
      }),
      catchError(e => {
        this._messageService.showError(e);
        return [];
      }),
      finalize(() => {
        this.progressBar.stop();
      })
    );
  }

  copyRooms(
    fromDwellingId: string,
    filteredActionModel: { filter: any; includedIds: string[]; excludedIds: string[]; affectedItemsCount: number },
    overWriteExisting: boolean
  ): Observable<any> {
    return this.client
      .post<any>(`${this.endpoint}/copyRooms-filter`, {
        fromRentalObjectId: fromDwellingId,
        filteredActionModel,
        overWriteExisting: overWriteExisting,
      })
      .pipe(
        catchError(e => {
          this._messageService.showError(e);
          return [];
        })
      );
  }

  protected createModel(data: any): Dwelling {
    const model = super.createModel(data);
    if (data.dwellingInspections instanceof Array) {
      model.dwellingInspections = data.dwellingInspections.map(item => new DwellingInspection(item));
    }
    return model;
  }

  getFilterDefinition(): Observable<{
    company: string; // 2026-05-25 Company can be removed when companyids has been used a couple of months. Was needed during a period to get information if the user has existing filter on company 
    companyIds: string[];
    property: string; // 2026-05-25 Property can be removed when propertyIds has been used a couple of months. Was needed during a period to get information if the user has existing filter on property 
    propertyIds: string[];
    selectedFields: string[];
    building: string; // 2026-05-25 Building can be removed when buildingIds has been used a couple of months. Was needed during a period to get information if the user has existing filter on building 
    buildingIds: string[];
    entrance: string; // 2026-05-25 Entrance can be removed when entranceIds has been used a couple of months. Was needed during a period to get information if the user has existing filter on entrance 
    entranceIds: string[];
    rentalObjectType: string;
    rentalObjectTypes: RentalObjectType[];
    rentalId: string;
    street: string;
    rentFromYear: number;
    rentFromMonth: number;
    roundRent: boolean;
    elevator: boolean;
    defaultFields: string[];
    lettingFeatureFilters: { lettingFeatureId: string; referenceType: number | null; matchAnyReferenceType: boolean }[];
    article: string;
    articles: Article[];
    formOfLease: number;
    householdElectricityPayment: number;
    heatingPayment: number;
    coldWaterPayment: number;
    hotWaterPayment: number;
    companyCustomPropertiesTextValues: { key: string; value: string }[];
    companyCustomPropertiesDateValues: { key: string; value: LocalDate }[];
    companyCustomPropertiesNumericValues: { key: string; value: string }[];
    companyCustomPropertiesBooleanValues: { key: string; value: boolean }[];
    rentalObjectCustomPropertiesTextValues: { key: string; value: string }[];
    rentalObjectCustomPropertiesDateValues: { key: string; value: LocalDate }[];
    rentalObjectCustomPropertiesNumericValues: { key: string; value: string }[];
    rentalObjectCustomPropertiesBooleanValues: { key: string; value: boolean }[];
    propertyCustomPropertiesTextValues: { key: string; value: string }[];
    propertyCustomPropertiesDateValues: { key: string; value: LocalDate }[];
    propertyCustomPropertiesNumericValues: { key: string; value: string }[];
    propertyCustomPropertiesBooleanValues: { key: string; value: boolean }[];
    buildingCustomPropertiesTextValues: { key: string; value: string }[];
    buildingCustomPropertiesDateValues: { key: string; value: LocalDate }[];
    buildingCustomPropertiesNumericValues: { key: string; value: string }[];
    buildingCustomPropertiesBooleanValues: { key: string; value: boolean }[];
  }> {
    return this.client.get<any>(`${this.endpoint}/filter`).pipe(
      map(res => {
        return {
          company: res.company || null,
          companyIds: res.companyIds || [],
          property: res.property || null,
          propertyIds: res.propertyIds || [],
          building: res.building || null,
          buildingIds: res.buildingIds || [],
          entrance: res.entrance || null,
          entranceIds: res.entranceIds || [],
          rentalObjectType: res.rentalObjectType || '',
          rentalObjectTypes: Array.isArray(res.rentalObjectTypes)
            ? res.rentalObjectTypes.map(x => Object.assign(new RentalObjectType(), x))
            : [],
          rentalId: res.rentalId || '',
          article: res.article || '',
          articles: Array.isArray(res.articles) ? res.articles.map(article => Object.assign(new Article(), article)) : [],
          street: res.street || '',
          elevator: res.elevator,
          rentFromYear: LocalDate.today().year,
          rentFromMonth: LocalDate.today().month,
          roundRent: res.roundRent ?? true,
          formOfLease: res.formOfLease || null,
          householdElectricityPayment: res.householdElectricityPayment ?? null,
          heatingPayment: res.heatingPayment ?? null,
          coldWaterPayment: res.coldWaterPayment ?? null,
          hotWaterPayment: res.hotWaterPayment ?? null,
          selectedFields: Array.isArray(res.selectedFields) ? res.selectedFields : this._defaultFilterFields,
          defaultFields: this._defaultFilterFields,
          lettingFeatureFilters: Array.isArray(res.lettingFeatureFilters) ? res.lettingFeatureFilters : [],
          companyCustomPropertiesTextValues: Array.isArray(res.companyCustomPropertiesTextValues)
            ? res.companyCustomPropertiesTextValues.map(v => ({
                key: v.key,
                value: v.value,
              }))
            : [],
          companyCustomPropertiesNumericValues: Array.isArray(res.companyCustomPropertiesNumericValues)
            ? res.companyCustomPropertiesNumericValues.map(v => ({
                key: v.key,
                value: v.value,
              }))
            : [],
          companyCustomPropertiesDateValues: Array.isArray(res.companyCustomPropertiesDateValues)
            ? res.companyCustomPropertiesDateValues.map(v => ({
                key: v.key,
                value: v.value,
              }))
            : [],
          companyCustomPropertiesBooleanValues: Array.isArray(res.companyCustomPropertiesBooleanValues)
            ? res.companyCustomPropertiesBooleanValues.map(v => ({
                key: v.key,
                value: v.value,
              }))
            : [],
          rentalObjectCustomPropertiesTextValues: Array.isArray(res.rentalObjectCustomPropertiesTextValues)
            ? res.rentalObjectCustomPropertiesTextValues.map(v => ({
                key: v.key,
                value: v.value,
              }))
            : [],
          rentalObjectCustomPropertiesDateValues: Array.isArray(res.rentalObjectCustomPropertiesDateValues)
            ? res.rentalObjectCustomPropertiesDateValues.map(v => ({
                key: v.key,
                value: v.value,
              }))
            : [],
          rentalObjectCustomPropertiesNumericValues: Array.isArray(res.rentalObjectCustomPropertiesNumericValues)
            ? res.rentalObjectCustomPropertiesNumericValues.map(v => ({
                key: v.key,
                value: v.value,
              }))
            : [],
          rentalObjectCustomPropertiesBooleanValues: Array.isArray(res.rentalObjectCustomPropertiesBooleanValues)
            ? res.rentalObjectCustomPropertiesBooleanValues.map(v => ({
                key: v.key,
                value: v.value,
              }))
            : [],
          propertyCustomPropertiesTextValues: Array.isArray(res.propertyCustomPropertiesTextValues)
            ? res.propertyCustomPropertiesTextValues.map(v => ({
                key: v.key,
                value: v.value,
              }))
            : [],
          propertyCustomPropertiesNumericValues: Array.isArray(res.propertyCustomPropertiesNumericValues)
            ? res.propertyCustomPropertiesNumericValues.map(v => ({
                key: v.key,
                value: v.value,
              }))
            : [],
          propertyCustomPropertiesDateValues: Array.isArray(res.propertyCustomPropertiesDateValues)
            ? res.propertyCustomPropertiesDateValues.map(v => ({
                key: v.key,
                value: v.value,
              }))
            : [],
          propertyCustomPropertiesBooleanValues: Array.isArray(res.propertyCustomPropertiesBooleanValues)
            ? res.propertyCustomPropertiesBooleanValues.map(v => ({
                key: v.key,
                value: v.value,
              }))
            : [],
          buildingCustomPropertiesTextValues: Array.isArray(res.buildingCustomPropertiesTextValues)
            ? res.buildingCustomPropertiesTextValues.map(v => ({
                key: v.key,
                value: v.value,
              }))
            : [],
          buildingCustomPropertiesNumericValues: Array.isArray(res.buildingCustomPropertiesNumericValues)
            ? res.buildingCustomPropertiesNumericValues.map(v => ({
                key: v.key,
                value: v.value,
              }))
            : [],
          buildingCustomPropertiesDateValues: Array.isArray(res.buildingCustomPropertiesDateValues)
            ? res.buildingCustomPropertiesDateValues.map(v => ({
                key: v.key,
                value: v.value,
              }))
            : [],
          buildingCustomPropertiesBooleanValues: Array.isArray(res.buildingCustomPropertiesBooleanValues)
            ? res.buildingCustomPropertiesBooleanValues.map(v => ({
                key: v.key,
                value: v.value,
              }))
            : [],
        };
      }),
      catchError(error => {
        console.log(error);
        return [];
      })
    );
  }

  export(filter: any, includedIds: string[], excludedIds: string[], affectedItemsCount: number): Observable<File> {
    return super.exportDataItemsFiltered(filter, includedIds, excludedIds, affectedItemsCount, 'Hyresobjekt.xlsx', true);
  }

  protected getStairCaseList(id: string, defaultFileName: string): Observable<File> {
    this.progressBar.start();
    return this.client.get(`${this.endpoint}/staircase-list/${id}`, { observe: 'response', responseType: 'blob' }).pipe(
      map((res: HttpResponse<Blob>) => {
        return this.toFile(res, defaultFileName);
      }),
      catchError(error => {
        this.notification.showError(error);
        return [];
      }),
      finalize(() => this.progressBar.stop())
    );
  }

  protected getBrokerInformation(id: string, calculationDate: LocalDate, defaultFileName: string): Observable<File> {
    this.progressBar.start();
    return this.client.get(`${this.endpoint}/broker-information/${id}`, { observe: 'response', responseType: 'blob' }).pipe(
      map((res: HttpResponse<Blob>) => {
        return this.toFile(res, defaultFileName);
      }),
      catchError(error => {
        this.notification.showError(error);
        return [];
      }),
      finalize(() => this.progressBar.stop())
    );
  }

  printBrokerInformation(model: BrokerInformation) {
    const formData = new FormData();
    formData.append('jsonData', JSON.stringify(model));
    this.progressBar.start();
    return this.client
      .post(`${this.endpoint}/broker-information`, JSON.stringify(model), { observe: 'response', responseType: 'blob' })
      .pipe(
        map((res: HttpResponse<Blob>) => {
          return this.toFile(res, model.fileName);
        }),
        catchError(error => {
          this.notification.showError(error);
          return [];
        }),
        finalize(() => this.progressBar.stop())
      );
  }

  printStairCaseList(id: string): Observable<File> {
    return this.getStairCaseList(id, 'Trapphuslista.docx');
  }

  getInspected(): Observable<Dwelling[]> {
    return this.client.get(`${this.endpoint}/inspected`).pipe(
      map(res => this.toModels(res)),
      catchError(error => {
        this.notification.showError(error);
        return [];
      })
    );
  }

  getPostTargetModels(buildingId: string): Observable<DwellingPostTarget[]> {
    this.progressBar.start();
    return this.client.get<DwellingPostTarget[]>(`${this.endpoint}/post-target/${buildingId}`).pipe(
      map(response => response.map(x => new DwellingPostTarget(x))),
      finalize(() => this.progressBar.stop())
    );
  }

  getHomeqOfficeDefaults(propertiesIds: string[]): Observable<HomeqDefaultsModel> {
    this.progressBar.start();
    return this.client.post<HomeqDefaultsModel>(`${this.endpoint}/homeq-defaults`, propertiesIds).pipe(
      map(val => new HomeqDefaultsModel(val)),
      finalize(() => this.progressBar.stop())
    );
  }

  getAndSendHomeqCompanies(rentalObjectId: string): Observable<HomeqCompanyOptions> {
    this.progressBar.start();
    return this.client.get<HomeqCompanyOptions>(`${this.endpoint}/homeq-offices/${rentalObjectId}`).pipe(
      map(val => new HomeqCompanyOptions(val)),
      finalize(() => this.progressBar.stop())
    );
  }

  getHomeqOffice(rentalObjectId: string): Observable<HQCompanyModel> {
    this.progressBar.start();
    return this.client.get<HQCompanyModel>(`${this.endpoint}/homeq-office/${rentalObjectId}`).pipe(
      map(val => new HQCompanyModel(val)),
      finalize(() => this.progressBar.stop())
    );
  }

  getDwellingsFiltered(filter: any, includedIds: string[], excludedIds: string[], affectedItemsCount: number): Observable<any> {
    return this.client.post<any>(`${this.endpoint}/filtered-dwellings`, {
      filter: this.prepareFilter(filter),
      includedIds: includedIds,
      excludedIds: excludedIds,
      affectedItemsCount: affectedItemsCount,
    });
  }
}
